import React from 'react';
import { sectionTitles, defaultPlaceholderData } from '../common/resumeConstants';
import { 
  checkHasRealData, 
  getCurrentData, 
  getPaperTypeText
} from '../common/resumeHelpers';
import { 
  CoreCompetenciesSection,
  SummarySection,
  PaperSection,
  getPlaceholderClass
} from '../common/ResumeComponents';

// Template 12 - 2열 테이블 레이아웃 (2.html 기반)
const Template12 = ({ resumeData, updatingField, language = 'kr' }) => {
  const titles = sectionTitles[language] || sectionTitles.kr;
  const hasRealData = checkHasRealData(resumeData);
  const currentData = getCurrentData(resumeData, defaultPlaceholderData, hasRealData);

  const SectionRow = ({ title, children, isLast = false }) => (
    <tr>
      <td className={`w-1/4 p-5 text-lg font-bold bg-gray-50 align-top ${!isLast ? 'border-b border-white' : ''}`}>
        {title}
      </td>
      <td className={`p-4 px-5 align-top ${!isLast ? 'border-b border-gray-200' : ''}`}>
        {children}
      </td>
    </tr>
  );

  return (
    <div className={`template-12 resume-container bg-white ${language === 'en' ? 'resume-english' : ''}`}>
      <table className="w-full border-collapse text-gray-800">
        <tbody>
          {/* Header Row */}
          <tr>
            <td colSpan="2" className="px-5 pt-5 pb-5 border-b-2 border-black">
              <h1 className={getPlaceholderClass(hasRealData.name, "text-4xl font-bold mb-2")}>
                {currentData.name}
              </h1>
              <p className={getPlaceholderClass(hasRealData.title, "text-lg text-gray-700")}>
                {currentData.title}
              </p>
            </td>
          </tr>

          {/* Contact Information */}
          <SectionRow title={titles.contact}>
            <table className="w-full border-collapse text-sm">
              <tbody>
                {currentData.phone && (
                  <tr>
                    <td className="w-20 font-bold py-1">{titles.phone}</td>
                    <td className={`py-1 ${getPlaceholderClass(hasRealData.phone)}`}>{currentData.phone}</td>
                  </tr>
                )}
                {currentData.email && (
                  <tr>
                    <td className="w-20 font-bold py-1">{titles.email}</td>
                    <td className={`py-1 ${getPlaceholderClass(hasRealData.email)}`}>{currentData.email}</td>
                  </tr>
                )}
                {currentData.address && (
                  <tr>
                    <td className="w-20 font-bold py-1">{titles.address}</td>
                    <td className={`py-1 ${getPlaceholderClass(hasRealData.address)}`}>{currentData.address}</td>
                  </tr>
                )}
                {currentData.socialLinks && currentData.socialLinks.length > 0 && currentData.socialLinks.map((social, index) => (
                  <tr key={index}>
                    <td className="w-20 font-bold py-1 capitalize">{social.type || social.platform}</td>
                    <td className="py-1">
                      <a href={social.url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                        {social.url.replace(/^https?:\/\/(www\.)?/, '')}
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </SectionRow>

          {/* Summary */}
          {currentData.summary && (
            <SectionRow title={titles.summary}>
              <SummarySection
                summary={currentData.summary}
                hasRealData={hasRealData}
                textClassName="text-sm leading-relaxed"
              />
            </SectionRow>
          )}

          {/* Key Strengths */}
          {currentData.keyStrengths && currentData.keyStrengths.length > 0 && (
            <SectionRow title={titles.keyStrengths}>
              <CoreCompetenciesSection
                keyStrengths={currentData.keyStrengths}
                hasRealData={hasRealData}
                bulletStyle="•"
                className="pl-5"
                textClassName="text-sm leading-relaxed mb-2"
              />
            </SectionRow>
          )}

          {/* Experience */}
          {currentData.experiences.length > 0 && (
            <SectionRow title={titles.experience}>
              {currentData.experiences.map((exp, index) => (
                <div key={index} className="mb-5">
                  <h3 className={getPlaceholderClass(hasRealData.experiences, "text-lg font-bold")}>
                    {exp.position}
                  </h3>
                  <p className={getPlaceholderClass(hasRealData.experiences, "text-sm text-gray-600 my-1")}>
                    {exp.company} | {exp.startDate} - {exp.endDate}
                  </p>
                  {exp.overallDescription && (
                    <p className={`text-sm leading-relaxed my-2 whitespace-pre-line ${getPlaceholderClass(hasRealData.experiences)}`}>
                      {exp.overallDescription}
                    </p>
                  )}
                  
                  {exp.projects && exp.projects.length > 0 && (
                    <div className="space-y-3 mt-3">
                      {exp.projects.map((project, projIndex) => (
                        <div key={projIndex} className="pl-4 border-l-[3px] border-gray-300">
                          <h4 className={getPlaceholderClass(hasRealData.experiences, "text-base font-bold mb-1")}>
                            {project.projectName}
                          </h4>
                          {project.projectDescription && (
                            <p className={`text-sm leading-relaxed whitespace-pre-line ${getPlaceholderClass(hasRealData.experiences)}`}>
                              {project.projectDescription}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </SectionRow>
          )}

          {/* Education */}
          {currentData.education.length > 0 && (
            <SectionRow title={titles.education}>
              {currentData.education.map((edu, index) => (
                <div key={index} className="text-sm">
                  <p className="mb-1">
                    <strong className={getPlaceholderClass(hasRealData.education, "text-base")}>
                      {edu.degree}
                    </strong>
                    , <span className={getPlaceholderClass(hasRealData.education)}>{edu.school}</span>
                  </p>
                  <p className={getPlaceholderClass(hasRealData.education, "text-gray-600 mb-1")}>
                    {edu.startDate} - {edu.endDate}
                  </p>
                  {edu.major && (
                    <p className={getPlaceholderClass(hasRealData.education)}>
                      {edu.major}
                    </p>
                  )}
                </div>
              ))}
            </SectionRow>
          )}

          {/* Papers */}
          {hasRealData.papers && currentData.papers.length > 0 && (
            <SectionRow title={titles.papers.toUpperCase()}>
              <PaperSection
                papers={currentData.papers}
                hasRealData={hasRealData}
                getPaperTypeText={getPaperTypeText}
                itemClassName="mb-3"
                titleClassName="text-base font-bold"
                authorsClassName="text-sm text-gray-600"
                journalClassName="text-sm"
              />
            </SectionRow>
          )}

          {/* Skills */}
          {currentData.skills.length > 0 && (
            <SectionRow title={titles.skills}>
              <p className={getPlaceholderClass(hasRealData.skills, "text-sm leading-relaxed")}>
                {currentData.skills.join(', ')}
              </p>
            </SectionRow>
          )}

          {/* Certificates */}
          {currentData.certificates.length > 0 && (
            <SectionRow title={titles.certificates} isLast={true}>
              {currentData.certificates.map((cert, index) => (
                <div key={index} className="text-sm mb-3 last:mb-0">
                  <p className="mb-1">
                    <strong className={getPlaceholderClass(hasRealData.certificates, "text-base")}>
                      {cert.name}
                    </strong>
                  </p>
                  <p className={getPlaceholderClass(hasRealData.certificates, "text-gray-600")}>
                    {cert.issuer} | {cert.date}
                  </p>
                </div>
              ))}
            </SectionRow>
          )}
        </tbody>
      </table>
    </div>
  );
};

export default Template12;